<template>
  <div class="preview-content">
    <p class="step-title">
      <span class="title__text">{{ $t(stepData[currentStep].titleKey) }}</span>
    </p>
    <button v-if="layer !== 'popup'"
            class="icon icon--maximize-size20"
            :class="{on: true}"
            @click="$emit('openShowPreviewLayer')"></button>
    <button v-else
            @click="$emit('onPreviewLayerClose')"
            class="on icon icon--minimize-size20"></button>

    <div class="content--wrap">
      <div class="content__header">
        <div class="button--wrap">
          <button type="button" class="btn__capture"
            :disabled="!canCapturePreview"
            @click="onCaptureClick">
            <i class="icon icon--retake-white"></i>
            <span v-if="captureState !== CAPTURE_STATE.done"
                  class="btn__text">{{ $t('common.screenShot') }}</span>
            <span v-else
                  class="btn__text">{{ $t('common.recapture') }}</span>
          </button>
          <span class="btn__info--wrap">
            <button type="button" @click="alertCustomTracking" class="btn__info">
              <span>{{ $t('common.analysisAreaGuide') }}</span>
            </button>
            <span class="in-ready"
              @click="alertCustomTracking">{{ $t('common.inReady2') }}</span>
          </span>
        </div>
        <!-- maybe use someday -->
        <ul v-if="false" class="info--wrap">
          <li>
            <i class="dot-blue"></i>
            <span>{{ $t('page.registration.preview.indicator.autoSet') }}</span>
          </li>
          <li>
            <i class="dot-yellow"></i>
            <span>{{ $t('page.registration.preview.indicator.customSet') }}</span>
          </li>
        </ul>
      </div>

      <div class="content__body">
        <div class="heatmap__device">
          <button v-for="item in deviceItems"
                  type="button"
                  class="item"
                  :key="item"
                  :class="{on: item === selectedDevice}"
                  @click="setDevice(item)">
            <span class="text">{{ item }}</span>
          </button>
        </div>
        <div v-if="!Object.keys(captureResultSet).length"
             class="capture-before">
          <p v-if="canCapturePreview" v-html="$t('page.registration.preview.screenShot.before')"></p>
          <div v-else class="disabled">
            <p class="title">
              <i class="icon icon--warning-red-size20"></i>
              <span>{{ $t('common.notice') }}</span>
            </p>
            <p class="desc" v-html="$t('page.registration.preview.screenShot.disabled')"></p>
          </div>
        </div>
        <div class="capture-after" ref="contentBody">
          <div v-for="deviceName in Object.keys(captureResultSet)"
               :key="deviceName"
               class="capture-container"
               :class="{ on: selectedDevice === deviceName }"
               :style="captureResultSet[deviceName].containerStyle">
            <div class="capture-failure" v-if="captureResultSet[deviceName].hasError">
              <p class="title">
                <i class="icon icon--warning-red-size20"></i>
                <span>{{ $t('page.registration.preview.screenShot.after.fail.title') }}</span>
              </p>
              <p class="desc first" v-html="$t('page.registration.preview.screenShot.after.fail.desc')"></p>
              <p class="desc" v-html="$t('page.registration.preview.screenShot.after.fail.desc2')"></p>
            </div>
            <template v-else>
              <div class="mask"></div>
              <div class="coords_rect"
                   v-for="(item, index) in captureResultSet[deviceName].coords"
                   :key="index"
                   :class="{ tracking_tag: item.isTrackingTag }"
                   :style="{ width: item.width, height: item.height, left: item.left, top: item.top }"></div>
            </template>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import httpClient from '@/libs/http-client'
import { CAPTURE_STATE, CAPTURE_SUCCESS } from '@/libs/apply-helper'
import { getDialog } from '@/libs/vue-utils'

const openDialog = getDialog('dialogType')

function getS3Path (path) {
  const parts = path.split(':')
  return `https://s3.ap-northeast-2.amazonaws.com/${parts[0]}/${parts[1]}`
}

export default {
  data () {
    return {
      captureResultSet: {}
    }
  },
  props: [
    'layer',
    'stepData',
    'currentStep',
    'captureState',
    'captureResult',
    'bMobile',
    'canCapturePreview',
    'selectedDevice'
  ],
  computed: {
    deviceItems () {
      if (this.bMobile) {
        return ['Phone']
      } else {
        return ['Desktop', 'Tablet', 'Phone']
      }
    }
  },
  watch: {
    'captureResult.tasks' (items) {
      if (items) {
        this.checkCaptureResult(items, true)
      } else {
        this.setDevice(null)
        this.captureResultSet = {}
      }
    }
  },
  created () {
    this.CAPTURE_STATE = CAPTURE_STATE
  },
  mounted () {
    if (this.captureResult && this.captureResult.tasks) {
      this.checkCaptureResult(this.captureResult.tasks)
    }
  },
  methods: {
    checkCaptureResult (tasks, updateDeviceName) {
      for (const item of tasks) {
        const deviceName = item.deviceName
        const statusCode = item.statusCode
        const isSuccess = statusCode === CAPTURE_SUCCESS
        const isFailed = statusCode < 0

        if (!this.captureResultSet[deviceName] && (isSuccess || isFailed)) {
          this.$set(this.captureResultSet, deviceName, {}) // prevent duplicated loadCaptureImage

          if (isFailed) {
            this.$set(this.captureResultSet[deviceName], 'hasError', true)
            if (updateDeviceName) {
              this.setDevice(deviceName)
            }
          } else {
            this.loadCaptureImage(item.imagePath, deviceName).then(() => {
              return this.loadCoords(item.jsonPath, deviceName)
            }).then(() => {
              if (updateDeviceName) {
                this.setDevice(deviceName)
              }
            })
          }
        }
      }
    },
    setDevice (value) {
      this.$emit('update:selectedDevice', value)
    },
    loadCaptureImage (path, deviceName) {
      const url = getS3Path(path)

      return new Promise((resolve, reject) => {
        const img = document.createElement('img')
        img.addEventListener('load', event => {
          const el = event.target
          const containerWidth = this.$refs.contentBody.getBoundingClientRect().width
          const ratio = el.width / el.height
          let scale = containerWidth / el.width
          let width = el.width
          let height = el.height

          if (scale < 1) {
            width = width * scale
            height = height * scale
          } else {
            scale = 1
          }

          this.captureResultSet[deviceName] = {
            imageUrl: url,
            captureWidth: el.width,
            captureHeight: el.height,
            captureRatio: ratio,
            captureScale: scale,
            containerStyle: {
              width: width + 'px',
              height: height + 'px',
              background: `url(${url}) no-repeat`,
              backgroundSize: '100%'
            },
            coords: null
          }
          resolve()
        })
        img.src = url
      })
    },
    loadCoords (path, deviceName) {
      const url = getS3Path(path)
      return httpClient.get(url).then(response => {
        const resultSet = this.captureResultSet[deviceName]
        const captureWidth = resultSet.captureWidth
        const captureHeight = resultSet.captureHeight
        const scale = resultSet.captureScale
        resultSet.coords = response.data.map(item => {
          let width = item[4]
          let height = item[5]
          let left = item[3]
          let top = item[0]

          const isValidRect = width > 0 && height > 0 &&
            top >= 0 && left >= 0 &&
            (left + width) <= captureWidth &&
            (top + height) <= captureHeight

          if (!isValidRect) {
            return null
          }

          return {
            width: (width * scale) + 'px',
            height: (height * scale) + 'px',
            left: (left * scale) + 'px',
            top: (top * scale) + 'px',
            isTrackingTag: !!item[7]
          }
        }).filter(item => !!item)
      })
    },
    alertCustomTracking () {
      openDialog('page.customTracking')
    },
    onCaptureClick () {
      if (!this.canCapturePreview) {
        return
      }

      const isRetake = this.captureState === CAPTURE_STATE.done
      this.$emit('openShowRenderingLayer', isRetake)
    }
  }
}
</script>

<style lang="scss" scoped>
.preview-content {
  height: 100%;
  .content--wrap {
    height: 100%;
    .content__header {
      margin-top: 20px;
      padding-left: 25px;
      padding-right: 25px;

      .btn__info--wrap {
        position: relative;
        display: inline-block;

        .in-ready {
          cursor: pointer;
          position: absolute;
          top: 50%;
          left: 100%;
          transform: translateY(-50%);
          white-space: nowrap;
          box-shadow: 0 8px 15px 0 rgba(0, 0, 0, 0.16);
          background-color: #1199af;
          color: #fff;
          font-size: 12px;
          text-align: center;
          padding: 6px 10px;
          border-radius: 4px;
          font-weight: 500;

          &:after {
            content: '';
            background-color: #1199af;
            z-index: -1;
            width: 10px;
            height: 10px;
            position: absolute;
            top: 7px;
            left: -3px;
            transform: rotate(-45deg);
          }
        }
      }

      .button--wrap {
        display: flex;
        button {
          height: 38px;
          border-radius: 20px;
          box-sizing: border-box;
          font-size: 14px;
          font-weight: 600;

          &.btn__capture {
            background-color: $brandColor;
            text-align: right;
            color: #ffffff;
            margin-right: 5px;
            position: relative;
            display: flex;
            align-items: center;

            .icon {
              position: absolute;
              left: 12px;
              pointer-events: none;
            }
            .btn__text {
              display: inline-block;
              width: 100%;
              padding-left: 39px;
              padding-right: 15px;
              box-sizing: border-box;
            }
            &[disabled] {
              background-color: #999;
              pointer-events: none;
            }

            .bubble {
              position: absolute;
              bottom: calc(100% + 10px);
              left: 0;
              width: 300px;

              /deep/ .text {
                white-space: normal;
                text-align: left;
              }
            }
          }
          &.btn__info {
            padding-left: 15px;
            padding-right: 15px;
            min-width: 150px;
            border: solid 1px #bbbbbb;
            background-color: #ffffff;
            text-align: center;
            color: #444444;
            opacity: 0.3;
          }
        }
      }

      .info--wrap {
        display: inline-block;
        float: right;
        margin-top: 18px;
        li {
          display: inline-block;

          &:nth-last-of-type(1) {
            margin-left: 25px;
          }
        }
        [class*='dot-'] {
          width: 10px;
          height: 10px;
          display: inline-block;
          border-radius: 50%;
          margin-right: 5px;
          vertical-align: bottom;
        }
        .dot-blue {
          border: solid 1px rgba(0, 0, 0, 0.16);
          background-color: #29e2ff;
        }
        .dot-yellow {
          border: solid 1px rgba(0, 0, 0, 0.15);
          background-color: #fff300;
        }
      }
    }

    .content__body {
      margin-top: 12px;
      background-color: #1b1b1d;
      height: calc(100% - 123px);
      position: relative;

      border-bottom-left-radius: 14px;
      border-bottom-right-radius: 14px;

      .heatmap__device {
        position: relative;
        z-index: 10;
        height: 36px;
        line-height: 34px;
        background-color: #0c0c0c;
        padding-left: 31px;
        button {
          height: 36px;
          margin-right: 43px;
          font-size: 14px;
          font-weight: 600;
          color: #888888;
          position: relative;
          &:hover {
            color: #fff;
          }
          &.on {
            color: $brandColor;

            &:after {
              border-color: #ec0047 transparent transparent transparent;
              width: 0;
              height: 0;
              border-style: solid;
              border-width: 12px 12px 0 0;
              display:block;
              position:absolute;
              bottom: -6px;
              left: 50%;
              content:'';
              transform: translateX(-50%) rotate(-135deg);
            }
          }
        }
      }

      .capture-before {
        @include positionXYcenter;
        width: 100%;
        padding: 20px;
        box-sizing: border-box;
        p {
          font-size: 13px;
          font-weight: 600;
          line-height: 1.54;
          text-align: center;
        }

        .disabled {
          .title {
            .icon {
              vertical-align: sub;
              margin-right: 10px;
            }
            font-size: 15px;
            text-align: center;
            color: #ffffff;
          }
          .desc {
            font-size: 13px;
            line-height: 1.54;
            color: #aaaaaa;
            position: relative;
            margin-top: 23px;
            margin-bottom: 17px;
            padding-top: 19px;
            &:before {
              position: absolute;
              top: 0;
              left: 50%;
              transform: translateX(-50%);
              content: '';
              display: inline-block;
              width: 60px;
              height: 1px;
              border-top: 1px solid #555;
            }
          }
        }
      }

      .capture-after {
        overflow: auto;
        overflow-x: hidden;
        position: absolute;
        top: 50px;
        bottom: 20px;
        left: 20px;
        right: 20px;
        box-sizing: border-box;
        border-bottom-left-radius: 17px;
        border-bottom-right-radius: 17px;
        font-weight: 500;
      }

      .capture-failure {
        margin-top: 60px;
        text-align: center;
        font-weight: 600;
        .title {
          .icon {
            vertical-align: sub;
          }
          font-size: 15px;
          text-align: center;
          color: #ffffff;
        }
        .desc {
          font-size: 13px;
          line-height: 1.54;
          color: #aaaaaa;
          &.first {
            position: relative;
            margin-top: 23px;
            margin-bottom: 17px;
            padding-top: 19px;
            &:before {
              position: absolute;
              top: 0;
              left: 50%;
              transform: translateX(-50%);
              content: '';
              display: inline-block;
              width: 60px;
              height: 1px;
              border-top: 1px solid #555;
            }
          }
          b {
            color: #fff;
          }
        }
      }

    }
  }
}

.capture-container {
  position: relative;
  margin: 0 auto;
  display: none;
  overflow: hidden;

  &.on {
    display: block;
  }

  .coords_rect {
    position: absolute;
    border: dashed 1px #29e2ff;
    background-color: rgba(41, 226, 255, 0.1);

    &.tracking_tag {
      border-color: #ffe30d;
      background-color: rgba(255, 227, 13, 0.2);
    }
  }
  .mask {
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background-color: #282b39;
    opacity: 0.7;
  }
}
</style>
